import { cms } from "../libs/api-url";
import axios from "@/libs/axios";

/**
 * CMS Service
 * Handles all admin CMS Master API calls (fixed set of pages, so no add/delete).
 *
 * API Endpoints:
 * - GET /api/v1/admin/cms/list - Get CMS page list
 * - GET /api/v1/admin/cms/get - Get a single CMS page with all language translations
 * - GET /api/v1/admin/cms/public/:slug - Public page content (no login)
 * - POST /api/v1/admin/cms/upload-image - Upload an image for the content editor
 * - POST /api/v1/admin/cms/add - Add a new CMS page
 * - DELETE /api/v1/admin/cms/delete - Delete a CMS page
 * - PUT /api/v1/admin/cms/update - Update a CMS page's translations
 */
class CmsService {
  /**
   * @name getList
   * @description Fetches the CMS page list.
   * @param {Object} params - Query parameters (page, limit, search_text)
   * @returns {Promise<Object>} The response data containing `cmsList` and `pagination`.
   */
  static async getList(params = {}) {
    try {
      const response = await axios.get(cms.list, { params });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name get
   * @description Fetches a single CMS page by UUID.
   * @param {string} uuid - CMS page UUID
   * @returns {Promise<Object>} The response data containing the page and its translations.
   */
  static async get(uuid) {
    try {
      const response = await axios.get(cms.get, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name getPublicPage
   * @description Fetches a CMS page's content without login (used by the public /cms/:slug page).
   * @param {string} slug - CMS page slug
   * @param {string} [languageCode] - Language code; the API falls back to the default language
   * @returns {Promise<Object>} The response data containing title, description and last_updated_at.
   */
  static async getPublicPage(slug, languageCode) {
    try {
      const response = await axios.get(`${cms.getPublicPage}/${encodeURIComponent(slug)}`, {
        params: languageCode ? { language_code: languageCode } : undefined,
      });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name add
   * @description Creates a new CMS page.
   * @param {Object} data - { slug, translations: [{ language_id, title, description }] }
   * @returns {Promise<Object>} The response data.
   */
  static async add(data) {
    try {
      const response = await axios.post(cms.add, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name updateStatus
   * @description Activates or deactivates a CMS page.
   * @param {string} uuid - CMS page UUID
   * @param {boolean} isActive - New status
   * @returns {Promise<Object>} The response data.
   */
  static async updateStatus(uuid, isActive) {
    try {
      const response = await axios.put(cms.updateStatus, { uuid, is_active: isActive });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name uploadImage
   * @description Uploads an image for the content editor and returns its public URL.
   * @param {File} file - Image file (JPG, PNG, GIF, WEBP, AVIF, BMP, SVG, ICO; max 10 MB)
   * @returns {Promise<string>} Public URL of the stored image.
   */
  static async uploadImage(file) {
    try {
      const formData = new FormData();
      formData.append("image", file);
      // The shared axios leaves files untouched and decrypts the top-level `url` in the response
      const response = await axios.post(cms.uploadImage, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      return response.data?.data?.url;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name delete
   * @description Deletes a CMS page by UUID.
   * @param {string} uuid - CMS page UUID
   * @returns {Promise<Object>} The response data.
   */
  static async delete(uuid) {
    try {
      const response = await axios.delete(cms.delete, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name update
   * @description Updates a CMS page's translations.
   * @param {Object} data - { uuid, translations: [{ language_id, title, description }] }
   * @returns {Promise<Object>} The response data.
   */
  static async update(data) {
    try {
      const response = await axios.put(cms.update, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }
}

export { CmsService };
